import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowDownToLine, Calendar, User, Loader2, Search, X, Hash,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import api from '../api/client';

// Phase 33 — Accounting & Orders export panel.
// Three modes:
//  A) Massiva    — download every transaction on the platform
//  B) Per data   — download by [start, end] interval
//  C) Per utente — autocomplete user, download their full history

const downloadBlob = (data, headers, fallback) => {
  const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  const cd = headers['content-disposition'] || headers['Content-Disposition'] || '';
  const match = cd.match(/filename="?([^";]+)"?/i);
  a.download = match ? match[1] : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const ExportCard = ({ icon: Icon, title, description, children, testId }) => (
  <div
    className="rounded-xl border border-border bg-card p-5"
    data-testid={testId}
  >
    <div className="mb-3 flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/20 gold-text">
        <Icon className="h-4 w-4" />
      </span>
      <h3 className="text-sm font-semibold uppercase tracking-wide gold-text">{title}</h3>
    </div>
    <p className="mb-4 text-xs text-muted-foreground">{description}</p>
    {children}
  </div>
);

const UserSearchAutocomplete = ({ onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const res = await api.get('/admin/users/search', { params: { q: q.trim(), limit: 12 } });
        setResults(res.data || []);
      } catch (_) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [q]);

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2" data-testid="orders-selected-user">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            @{selected.username} <span className="ml-1 text-xs text-muted-foreground">#{selected.user_number}</span>
          </p>
          <p className="truncate text-[11px] text-muted-foreground">{selected.full_name} · {selected.email}</p>
        </div>
        <button
          onClick={() => { setSelected(null); onSelect(null); setQ(''); }}
          className="ml-3 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-background"
          data-testid="orders-clear-user"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <span className="absolute left-3 text-muted-foreground"><Search className="h-3.5 w-3.5" /></span>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome, email o ID (1001+)…"
          className="h-9 pl-8 text-sm bg-background"
          data-testid="orders-user-search-input"
        />
        {searching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {results.length > 0 && (
        <ul
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg"
          data-testid="orders-user-search-results"
        >
          {results.map((u) => (
            <li key={u.id}>
              <button
                onClick={() => { setSelected(u); onSelect(u); setQ(''); setResults([]); }}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-card"
                data-testid={`orders-user-result-${u.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold">@{u.username}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{u.full_name} · {u.email}</p>
                </div>
                <span className="ml-2 shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold gold-text">
                  #{u.user_number ?? '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AccountingOrdersPanel = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(null); // 'all' | 'date' | 'user' | null
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [user, setUser] = useState(null);
  const [includePayouts, setIncludePayouts] = useState(true);
  const [includeRefunds, setIncludeRefunds] = useState(true);

  const exportCsv = useCallback(async (mode, params, fallback) => {
    setBusy(mode);
    try {
      const res = await api.get('/admin/orders/export.csv', {
        params: {
          ...params,
          include_payouts: includePayouts,
          include_refunds: includeRefunds,
        },
        responseType: 'blob',
      });
      downloadBlob(res.data, res.headers, fallback);
      toast({ title: 'CSV scaricato', description: fallback });
    } catch (e) {
      toast({
        title: 'Esportazione fallita',
        description: e.response?.data?.detail || 'Riprova più tardi.',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  }, [includePayouts, includeRefunds, toast]);

  return (
    <div className="space-y-4" data-testid="accounting-orders-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Esportazione contabile</p>
          <h2 className="mt-1 text-lg font-semibold">Ordini & movimenti CSV</h2>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="orders-include-payouts">
            <input
              type="checkbox"
              checked={includePayouts}
              onChange={(e) => setIncludePayouts(e.target.checked)}
              className="h-3.5 w-3.5 accent-amber-400"
            />
            Includi payout
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="orders-include-refunds">
            <input
              type="checkbox"
              checked={includeRefunds}
              onChange={(e) => setIncludeRefunds(e.target.checked)}
              className="h-3.5 w-3.5 accent-amber-400"
            />
            Includi refund
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* A — Massiva */}
        <ExportCard
          icon={ArrowDownToLine}
          title="Esportazione massiva"
          description="Tutti i movimenti economici della piattaforma, senza filtri. Ideale per archivio fiscale."
          testId="orders-export-all-card"
        >
          <Button
            onClick={() => exportCsv('all', {}, 'nymia_accounting_all.csv')}
            disabled={busy === 'all'}
            className="h-9 w-full gold-gradient text-xs"
            data-testid="orders-export-all-btn"
          >
            {busy === 'all' ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Esportando…</span>
            ) : (
              <span className="inline-flex items-center gap-1.5"><ArrowDownToLine className="h-3.5 w-3.5" /> Scarica CSV completo</span>
            )}
          </Button>
        </ExportCard>

        {/* B — Per intervallo date */}
        <ExportCard
          icon={Calendar}
          title="Per intervallo date"
          description="Seleziona inizio e fine. Lascia un campo vuoto per limite aperto."
          testId="orders-export-date-card"
        >
          <div className="space-y-2">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Inizio</label>
              <Input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="h-9 text-sm bg-background"
                data-testid="orders-export-date-start"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Fine</label>
              <Input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="h-9 text-sm bg-background"
                data-testid="orders-export-date-end"
              />
            </div>
            <Button
              onClick={() => {
                if (!start && !end) {
                  toast({ title: 'Inserisci almeno una data', variant: 'destructive' });
                  return;
                }
                const params = {};
                if (start) params.start = `${start}T00:00:00Z`;
                if (end) params.end = `${end}T23:59:59Z`;
                exportCsv('date', params, `nymia_accounting_${start || 'min'}_${end || 'now'}.csv`);
              }}
              disabled={busy === 'date'}
              className="h-9 w-full bg-black hover:bg-neutral-800 text-xs text-white"
              data-testid="orders-export-date-btn"
            >
              {busy === 'date' ? (
                <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Esportando…</span>
              ) : (
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Scarica intervallo</span>
              )}
            </Button>
          </div>
        </ExportCard>

        {/* C — Per singolo utente */}
        <ExportCard
          icon={User}
          title="Per utente / creator"
          description="Cerca per nome, cognome, email o ID (1001+). Esporta solo la sua storia."
          testId="orders-export-user-card"
        >
          <div className="space-y-2">
            <UserSearchAutocomplete onSelect={setUser} />
            <Button
              onClick={() => {
                if (!user) {
                  toast({ title: 'Seleziona prima un utente', variant: 'destructive' });
                  return;
                }
                exportCsv(
                  'user',
                  { user_id: user.id },
                  `nymia_accounting_user${user.user_number || ''}.csv`,
                );
              }}
              disabled={busy === 'user' || !user}
              className="h-9 w-full bg-black hover:bg-neutral-800 text-xs text-white disabled:opacity-50"
              data-testid="orders-export-user-btn"
            >
              {busy === 'user' ? (
                <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Esportando…</span>
              ) : (
                <span className="inline-flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> Scarica utente</span>
              )}
            </Button>
          </div>
        </ExportCard>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Il CSV include 20 colonne: ID utente, nome, cognome, email, tipo, ordine, importo €,
        crediti acquistati/spesi/ricevuti, data, oggetto, categoria, controparte, stato, metodo,
        commissione Nymia, netto Voce Pro, stato payout, ID transazione. Categorie: ricarica, post premium
        (image/video), live ticket, scheduled live, tip, chiamate, refund, payout.
      </p>
    </div>
  );
};

export default AccountingOrdersPanel;
